import { el } from '../../core/dom.js';
import { openModal } from '../../components/modal.js';
import { toast } from '../../components/toast.js';
import {
  addRecipient, blankForm, destinationOf, destOn, formFromRow, monthDayOf,
  payloadFromForm, removeRecipient, runToast, toggleWeekday,
} from './model.js';
import { buildForm, paintDest, paintEmails, paintWhen } from './fields.js';
import { removeSchedule, runSchedule, saveSchedule, setEnabled } from './actions.js';

export function openScheduleEditor(opts) {
  const ctx = {
    opts,
    form: opts.row ? formFromRow(opts.row, opts.me) : blankForm(opts.me),
    busy: false,
    closed: false,
    confirmDelete: false,
  };
  const built = buildForm(ctx.form, opts.saved || [], opts.productName || 'This product');
  Object.assign(ctx, built, { body: built.root });
  paintEmails(ctx.emailHost, ctx.form.recipients);
  paintDest(ctx.destHost, ctx.emailHost, ctx.form);
  paintWhen(ctx.whenHost, ctx.form);
  syncDest(ctx);
  syncPause(ctx);
  ctx.modal = openModal({
    title: ctx.form.id ? 'Edit schedule' : 'New schedule',
    className: 'sched-modal',
    body: ctx.body,
    onClose() {
      ctx.closed = true;
      if (opts.onClose) opts.onClose();
    },
  });
  bind(ctx);
  const prompt = ctx.body.querySelector('#sched-prompt');
  if (prompt) prompt.focus();
  return ctx.modal;
}

function bind(ctx) {
  ctx.body.addEventListener('click', (event) => onClick(ctx, event));
  ctx.body.addEventListener('submit', (event) => {
    event.preventDefault();
    saveOnly(ctx);
  });
  ctx.body.addEventListener('keydown', (event) => onKey(ctx, event));
  const saved = ctx.body.querySelector('[name="saved"]');
  if (!saved) return;
  saved.addEventListener('change', () => {
    const prompt = ctx.body.querySelector('#sched-prompt');
    if (prompt && saved.value) prompt.value = saved.value;
    syncPromptLabel(ctx);
  });
}

function onKey(ctx, event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  if (event.key === 'Enter' && target.getAttribute('name') === 'email-new') {
    event.preventDefault();
    onAddEmail(ctx);
    return;
  }
  if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
  if (target.getAttribute('role') !== 'radio') return;
  const group = target.closest('[role="radiogroup"]');
  if (!group) return;
  const radios = [...group.querySelectorAll('[role="radio"]')];
  const index = radios.indexOf(target);
  if (index < 0) return;
  event.preventDefault();
  const delta = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
  const next = radios[(index + delta + radios.length) % radios.length];
  next.click();
}

function onClick(ctx, event) {
  const node = event.target instanceof Element ? event.target : null;
  const btn = node && node.closest('[data-act]');
  if (!btn || ctx.busy) return;
  const act = btn.getAttribute('data-act');
  if (act !== 'delete' && ctx.confirmDelete) {
    ctx.confirmDelete = false;
    syncDelete(ctx);
  }
  if (act === 'cadence') onCadence(ctx, btn.getAttribute('data-cadence'));
  else if (act === 'day') onDay(ctx, Number(btn.getAttribute('data-iso')));
  else if (act === 'month') onMonth(ctx, btn.getAttribute('data-day'));
  else if (act === 'dest') onDest(ctx, btn.getAttribute('data-dest'), btn.checked);
  else if (act === 'add-email') onAddEmail(ctx);
  else if (act === 'unemail') onRemoveEmail(ctx, btn.getAttribute('data-email') || '');
  else if (act === 'run') runNow(ctx);
  else if (act === 'pause') pause(ctx);
  else if (act === 'delete') armDelete(ctx);
}

function readFields(ctx) {
  const prompt = ctx.body.querySelector('#sched-prompt');
  const time = ctx.body.querySelector('[name="run_time"]');
  const save = ctx.body.querySelector('[name="save_question"]');
  if (prompt) ctx.form.prompt = prompt.value;
  if (time && time.value) ctx.form.run_time = time.value;
  ctx.form.save_question = !!(save && save.checked);
}

function showError(ctx, message) {
  ctx.errorNode.textContent = message || '';
  ctx.errorNode.hidden = !message;
}

function takePendingEmail(ctx) {
  if (!destOn(ctx.form.destination, 'email')) return true;
  const pending = ctx.body.querySelector('[name="email-new"]');
  const typed = pending ? pending.value.trim() : '';
  if (!typed) return true;
  const added = addRecipient(ctx.form.recipients, typed);
  if (added.error) {
    showError(ctx, added.error);
    return false;
  }
  ctx.form.recipients = added.recipients;
  pending.value = '';
  paintEmails(ctx.emailHost, ctx.form.recipients);
  return true;
}

function onCadence(ctx, cadence) {
  if (!cadence || cadence === ctx.form.cadence) return;
  readFields(ctx);
  ctx.form.cadence = cadence;
  markPressed(ctx.body, 'cadence', 'data-cadence', cadence);
  paintWhen(ctx.whenHost, ctx.form);
}

function onDay(ctx, iso) {
  readFields(ctx);
  const next = toggleWeekday(ctx.form.weekdays, iso);
  ctx.form.weekdays = next.weekdays;
  showError(ctx, next.error || '');
  paintWhen(ctx.whenHost, ctx.form);
  focusAct(ctx.whenHost, 'day', 'data-iso', String(iso));
}

function onMonth(ctx, day) {
  readFields(ctx);
  ctx.form.month_day = monthDayOf(day);
  showError(ctx, '');
  paintWhen(ctx.whenHost, ctx.form);
  focusAct(ctx.whenHost, 'month', 'data-day', ctx.form.month_day);
}

function onDest(ctx, key, checked) {
  readFields(ctx);
  const email = key === 'email' ? !!checked : destOn(ctx.form.destination, 'email');
  const docs = key === 'kb' ? !!checked : destOn(ctx.form.destination, 'kb');
  ctx.form.destination = destinationOf(email, docs);
  showError(ctx, '');
  paintDest(ctx.destHost, ctx.emailHost, ctx.form);
  syncDest(ctx);
  const box = ctx.destHost.querySelector(`[data-dest="${key}"]`);
  if (box) box.focus();
}

function onAddEmail(ctx) {
  const pending = ctx.body.querySelector('[name="email-new"]');
  if (!pending || !pending.value.trim()) {
    showError(ctx, 'Enter a valid email address');
    return;
  }
  if (!takePendingEmail(ctx)) return;
  showError(ctx, '');
  const input = ctx.body.querySelector('[name="email-new"]');
  if (input) input.focus();
}

function onRemoveEmail(ctx, email) {
  const next = removeRecipient(ctx.form.recipients, email, ctx.form.destination);
  if (next.error) {
    showError(ctx, next.error);
    return;
  }
  ctx.form.recipients = next.recipients;
  showError(ctx, '');
  paintEmails(ctx.emailHost, ctx.form.recipients);
}

function setBusy(ctx, message) {
  ctx.body.querySelectorAll('button, input, select, textarea').forEach((node) => {
    if (node.classList && node.classList.contains('sched-cta')) return;
    node.disabled = !!ctx.busy;
  });
  syncDest(ctx);
  ctx.busyNote.textContent = message || '';
  ctx.busyNote.hidden = !message;
}

function syncDest(ctx) {
  const off = !destOn(ctx.form.destination, 'email') && !destOn(ctx.form.destination, 'kb');
  if (ctx.destWarn) ctx.destWarn.hidden = !off;
  const save = ctx.body.querySelector('.sched-cta');
  if (save) save.disabled = !!ctx.busy || off;
}

function syncPromptLabel(ctx) {
  const saved = ctx.body.querySelector('[name="saved"]');
  const label = ctx.body.querySelector('[data-prompt-label]');
  if (!label) return;
  label.classList.toggle('sched-sr', !!(saved && saved.value));
}

async function persist(ctx) {
  if (ctx.busy) return null;
  readFields(ctx);
  if (!takePendingEmail(ctx)) return null;
  const built = payloadFromForm(ctx.form, ctx.opts.productId);
  if (built.error) {
    showError(ctx, built.error);
    return null;
  }
  ctx.busy = true;
  setBusy(ctx, 'Saving…');
  const result = await saveSchedule(ctx.form.id, built.body);
  ctx.busy = false;
  if (!ctx.closed) setBusy(ctx, '');
  if (!result || result.error) {
    if (!ctx.closed) showError(ctx, (result && result.error) || 'Could not save the schedule');
    return null;
  }
  if (result.schedule && result.schedule.id) ctx.form.id = result.schedule.id;
  if (!ctx.closed) {
    showError(ctx, '');
    ensureEditActions(ctx);
  }
  return result;
}

async function saveOnly(ctx) {
  const result = await persist(ctx);
  if (!result) return;
  if (!ctx.closed) {
    toast('Schedule saved');
    ctx.modal.close();
  }
  if (ctx.opts.onSaved) await ctx.opts.onSaved();
}

async function runNow(ctx) {
  const saved = await persist(ctx);
  if (!saved || ctx.closed) return;
  if (!ctx.form.id) {
    showError(ctx, 'Could not save the schedule before running');
    return;
  }
  ctx.busy = true;
  setBusy(ctx, 'Running… this can take a minute.');
  const result = await runSchedule(ctx.form.id);
  ctx.busy = false;
  if (!ctx.closed) setBusy(ctx, '');
  if (!result || result.error) {
    const message = (result && result.error) || 'Run failed';
    if (!ctx.closed) showError(ctx, String(message).replace(/^Skipped:\s*/i, ''));
    toast(String(message).replace(/^Skipped:\s*/i, ''), 'error');
    if (ctx.opts.onSaved) await ctx.opts.onSaved();
    return;
  }
  toast(runToast(result, ctx.form.destination));
  if (!ctx.closed) ctx.modal.close();
  if (ctx.opts.onSaved) await ctx.opts.onSaved();
}

async function pause(ctx) {
  if (!ctx.form.id || ctx.busy) return;
  const next = ctx.form.enabled === false;
  ctx.busy = true;
  setBusy(ctx, next ? 'Resuming…' : 'Pausing…');
  const result = await setEnabled(ctx.form.id, next);
  ctx.busy = false;
  if (!ctx.closed) setBusy(ctx, '');
  if (!result || result.error) {
    if (!ctx.closed) showError(ctx, (result && result.error) || 'Could not update the schedule');
    return;
  }
  ctx.form.enabled = result.schedule ? result.schedule.enabled !== false : next;
  syncPause(ctx);
  toast(ctx.form.enabled ? 'Schedule resumed' : 'Schedule paused');
  if (ctx.opts.onSaved) await ctx.opts.onSaved();
}

function armDelete(ctx) {
  if (!ctx.form.id) return;
  if (!ctx.confirmDelete) {
    ctx.confirmDelete = true;
    syncDelete(ctx);
    return;
  }
  doDelete(ctx);
}

async function doDelete(ctx) {
  ctx.busy = true;
  setBusy(ctx, 'Deleting…');
  const result = await removeSchedule(ctx.form.id);
  ctx.busy = false;
  if (!ctx.closed) setBusy(ctx, '');
  if (!result || result.error) {
    if (!ctx.closed) showError(ctx, (result && result.error) || 'Could not delete the schedule');
    return;
  }
  toast('Schedule deleted');
  if (!ctx.closed) ctx.modal.close();
  if (ctx.opts.onSaved) await ctx.opts.onSaved();
}

function ensureEditActions(ctx) {
  if (!ctx.form.id || ctx.body.querySelector('[data-act="delete"]')) return;
  const bar = ctx.body.querySelector('.sched-form-actions');
  if (!bar) return;
  bar.prepend(el('button', { type: 'button', class: 'sched-btn', 'data-act': 'delete' }, 'Delete'));
  bar.prepend(el('button', { type: 'button', class: 'sched-btn', 'data-act': 'pause' }, 'Pause'));
  syncPause(ctx);
}

function syncPause(ctx) {
  const btn = ctx.body.querySelector('[data-act="pause"]');
  if (!btn) return;
  btn.textContent = ctx.form.enabled === false ? 'Resume' : 'Pause';
}

function syncDelete(ctx) {
  const btn = ctx.body.querySelector('[data-act="delete"]');
  if (!btn) return;
  btn.textContent = ctx.confirmDelete ? 'Delete this schedule' : 'Delete';
  btn.classList.toggle('is-armed', ctx.confirmDelete);
}

function markPressed(root, act, attr, value) {
  root.querySelectorAll(`[data-act="${act}"]`).forEach((btn) => {
    const on = btn.getAttribute(attr) === value;
    btn.classList.toggle('is-on', on);
    btn.setAttribute('aria-checked', on ? 'true' : 'false');
    btn.tabIndex = on ? 0 : -1;
  });
}

function focusAct(root, act, attr, value) {
  const btn = root.querySelector(`[data-act="${act}"][${attr}="${value}"]`);
  if (btn) btn.focus();
}
